import { RestUrl } from "@/commons/constants/RestUrl";
import UrlFormatter from "@/commons/utils/UrlFormatter";
import CommonService from "@/commons/services/CommonService";
import LoginUser from "@/store/LoginUser";
import { GroupType } from "./SendMessageGroups";
import { HitomeqErrorType } from "@/commons/models/SystemError";
import { SenderType } from "./Families";

/**
 * ### 文書送付 API 操作クラス
 */
export default class SendDocuments {
  /** 共通サービス */
  private commonService: CommonService;

  /**
   * ### コンストラクター
   *
   * - 共通サービスが渡されればそれを使用する
   * - 共通サービスが渡されなければ新しいインスタンスを作る
   *
   * @param commonService 共通サービス
   */
  constructor(commonService?: CommonService) {
    this.commonService = commonService ? commonService : new CommonService();
    return;
  }

  /**
   * ### GET SendDocument
   */
  public async get(userStore: LoginUser, mainId: number): Promise<Array<SendDocumentGetModel>> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      mainId: mainId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.SendDocument, formatParams);
    const response = await this.commonService.executeGet<Array<SendDocumentGetModel>>(userStore, requestUrl, this.constructor.name, null, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * ### GETS SendDocuments
   */
  public async gets(userStore: LoginUser, queryParams?: SendDocumentGetsQuery): Promise<Array<SendDocumentGetModel>> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.SendDocuments, formatParams);

    const params: any = queryParams ? queryParams : {};
    const response = await this.commonService.executeGet<Array<SendDocumentGetModel>>(userStore, requestUrl, this.constructor.name, params, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * ### GET SendDocumentsCount
   */
  public async getCount(userStore: LoginUser, params: SendDocumentGetCountQuery): Promise<number> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.SendDocuments, formatParams);
    const response = await this.commonService.executeGet<{ count: number }>(userStore, `${requestUrl}/count`, this.constructor.name, params, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data.count;
  }

  /**
   * ### POST SendDocument
   */
  public async post(userStore: LoginUser, requestBody: SendDocumentPostModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.SendDocuments, formatParams);
    const response = await this.commonService.executePost<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * PATCH SendDocument
   */
  public async patch(userStore: LoginUser, mainId: number, requestBody: SendDocumentPatchModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      mainId: mainId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.SendDocument, formatParams);
    const response = await this.commonService.executePatch<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * DELETE SendDocument
   */
  public async delete(userStore: LoginUser, mainId: number): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      mainId: mainId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.SendDocument, formatParams);
    const response = await this.commonService.executeDelete<any>(userStore, requestUrl, this.constructor.name, null, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * POST SendDocument to families
   */
  public async sendToFamilies(userStore: LoginUser, mainId: number, requestBody: SendDocumentSendPostModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      mainId: mainId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.SendDocumentToFamilies, formatParams);
    const response = await this.commonService.executePost<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * POST remind SendDocument to families
   */
  public async sendRemindToFamilies(userStore: LoginUser, mainId: number, requestBody: SendDocumentRemindPostModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      mainId: mainId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.SendDocumentRemindToFamilies, formatParams);
    const response = await this.commonService.executePost<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * POST SendDocument to otherUserGroups
   */
  public async sendToOtherUserGroups(userStore: LoginUser, mainId: number, requestBody: SendDocumentSendPostModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      mainId: mainId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.SendDocumentToOtherUserGroups, formatParams);
    const response = await this.commonService.executePost<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * POST remind SendDocument to otherUserGroups
   */
  public async sendRemindToOtherUserGroups(userStore: LoginUser, mainId: number, requestBody: SendDocumentRemindPostModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      mainId: mainId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.SendDocumentRemindToOtherUserGroups, formatParams);
    const response = await this.commonService.executePost<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * GET SendDocumentReplies
   */
  public async getReplies(userStore: LoginUser, mainId: number): Promise<Array<SendDocumentReplyGetModel>> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      mainId: mainId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.SendDocumentReplies, formatParams);
    const response = await this.commonService.executeGet<Array<SendDocumentReplyGetModel>>(userStore, requestUrl, this.constructor.name, null, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * GET SendDocumentRepliesInResident
   */
  public async getRepliesInResident(userStore: LoginUser, mainId: number, residentId: number): Promise<Array<SendDocumentReplyGetModel>> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      mainId: mainId,
      sortKey: residentId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.SendDocumentRepliesInResident, formatParams);
    const response = await this.commonService.executeGet<Array<SendDocumentReplyGetModel>>(userStore, requestUrl, this.constructor.name, null, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }
}

export interface SendDocumentGetsQuery {
  /** 開始インデックス */
  startIndex?: number;
  /** 終了インデックス */
  endIndex?: number;
  /** 開始日時 */
  start?: string;
  /** 終了日時 */
  end?: string;
  /** 親レコード(mainId=0)のものだけ取得するか */
  onlyParent?: boolean;
  /** 送信済みのみを取得するか */
  onlySended?: boolean;
}

export interface SendDocumentGetCountQuery {
  /** 親レコード(mainId=0)のものだけ取得するか */
  onlyParent?: boolean;
  /** 送信済みのみを取得するか */
  onlySended?: boolean;
  /** 開始日時 */
  start?: string;
  /** 終了日時 */
  end?: string;
}

export interface SendDocumentGetModel {
  /** 施設ID */
  facilityId: number;
  /** 文書送付を示すID */
  id: string;
  /** 文書送付メインID */
  mainId: number;
  /** 文書送付サブID */
  subId: number;
  /** 送付済み。true：送付済み、false：未送信 */
  isSended: boolean;
  /** 公開状態。true：公開中、false：非公開 */
  isPublic: boolean;
  /** 宛先種別 */
  type: Type;
  /** 宛先 */
  receivers: Array<SendDocumentFamilyReceiver | SendDocumentOtherUserReceiver>;
  /** 宛先(LINE) */
  receiversByLine: Array<SendDocumentFamilyReceiver | SendDocumentOtherUserReceiver>;
  /** 宛先(MAIL) */
  receiversByMail: Array<SendDocumentFamilyReceiver | SendDocumentOtherUserReceiver>;
  /** 宛先グループの情報 */
  sendMessageGroupInfos: Array<SendMessageGroupInfo> | null;
  /** 利用者グループ情報 */
  residentGroupInfos: Array<ResidentGroupInfo> | null;
  /** 施設関係者グループ情報 */
  otherUserGroupInfos: Array<GroupOtherUserGroupInfo> | null;
  /** 文書単位 */
  unit: Unit;
  /** 文書種類カテゴリID */
  categoryId: number;
  /** タイトル */
  title: string;
  /** 本文 */
  text: string;
  /** 確認しましたボタンを付与するか */
  needConfirm: boolean;
  /** 同意しましたボタンを付与するか */
  needAgreement: boolean;
  /** 文書の S3URL */
  fileUrls: Array<FileUrl>;
  /** 送信したユーザーの ID */
  sender: string | null;
  /** 送信日時 */
  sendDatetime: number | null;
  /** 送信に成功した LINE ユーザーのリスト */
  successUsers: Array<ReceiverDetail> | null;
  /** 送信に失敗した LINE ユーザーのリスト */
  failedUsers: Array<ReceiverDetail> | null;

  /** 論理削除フラグ */
  deleteFlag: boolean;
  /** 最終更新日 */
  updated: number;
  /** 作成した日付(YYYYMMDDhhmmssSSS) */
  created: number;
}

/**
 * ### 種別
 */
export enum Type {
  /** 不明 */
  UNKNOWN = -1,
  /** 家族全員 */
  ALL_FAMILY = 0,
  /** 家族個別 */
  SELECTED_FAMILY = 1,
  /** 施設関係者個別 */
  SELECTED_OTHER_USER = 2,
  /** 宛先グループ */
  SEND_MESSAGE_GROUP = 3,
  /** 利用者グループ */
  RESIDENT_GROUP = 4,
  /** 施設関係者グループ */
  OTHER_USER_GROUP = 5
}

/**
 * ### 文書単位
 */
export enum Unit {
  /** 不明 */
  UNKNOWN = -1,
  /** 全員同じ */
  SAME = 0,
  /** 利用者単位 */
  SEPARATE = 1
}

/**
 * ### 文書送付の受信者情報(家族)
 */
export interface SendDocumentFamilyReceiver {
  /** ソートキー (residentId)_(familyId) */
  residentsSortKey: string;
  /** 利用者を示す ID */
  id: number;
  /** 利用者グループID */
  groupId: number;
  /** 利用者グループ名 */
  groupName: string;
  /** 利用者の名前 */
  name: string;
  /** 利用者の名前（ふりがな） */
  kanaName: string;
  /** 部屋ID */
  roomId: number;
  /** 部屋名 */
  room: string;
  /** 送信種別 */
  senderType: SenderType;
  /** 家族を示す ID */
  familyId: number;
  /** 家族の名前 */
  familyName: string;
  /** 家族の電話番号 */
  familyTel: string;
  /** LINEID */
  familyLineId: string | null;
  /** メールアドレス */
  familyMailaddress: string | null;
  /** 続柄 */
  familyRelation: string;
  /** 役割 */
  familyPosition: string | null;
  /** 保険者番号_被保険者番号で作るキー */
  insuredNumberKey: string | null;
}

/**
 * ### 文書送付の受信者情報(施設関係者)
 */
export interface SendDocumentOtherUserReceiver {
  /** {id}_{memberId}で作るソートキー */
  otherUserGroupsSortKey: string;
  /** グループを一意に識別する ID */
  id: number;
  /** グループ名 */
  name: string;
  /** グループの種類の ID。otherUserGroupTypes の id とひもづく */
  groupTypeId: number;
  /** グループ種類名 */
  groupTypeName: string;
  /** 送信種別 */
  senderType: SenderType;
  /** グループ内のメンバーを一意に識別する ID */
  memberId: number;
  /** メンバー名 */
  memberName: string;
  /** メンバー電話番号 */
  memberTel: string;
  /** メンバー所属 */
  memberOrganization: string;
  /** LINEID */
  memberLineId: string | null;
  /** メールアドレス */
  memberMailaddress: string | null;
}

/**
 * ### 宛先グループ情報
 */
export interface SendMessageGroupInfo {
  /** ソートキー */
  sendMessageGroupsSortKey: string;
  /** グループを一意に識別する ID */
  id: number;
  /** グループ名 */
  name: string;
  /** 送信宛先種別 */
  type: GroupType;
  /** グループメンバーのソートキー */
  memberSortKeys: Array<string>;
}

export interface ResidentGroupInfo {
  /** ID */
  id: number;
  /** グループ名 */
  name: string;
  /** 利用者グループに所属する利用者家族のソートキー */
  residentsSortKeys: string[];
}

export interface GroupOtherUserGroupInfo {
  /** 施設関係者グループソートキー */
  otherUserGroupsSortKey: string;
  /** グループID */
  id: number;
  /** グループ名 */
  name: string;
  /** 施設関係者グループメンバーのソートキー */
  memberSortKeys: string[];
}

/**
 * ### ファイルURL情報
 */
export interface FileUrl {
  /** ユーザーを特定するソートキー */
  sortKey: string;
  /** ファイルのS3格納先URL */
  files: Array<SendDocumentFile>;
}

export interface SendDocumentFile {
  url: string;
  name: string;
}

export interface SendDocumentPostModel {
  /** 宛先種別 */
  type: Type | null;
  /** 宛先 */
  receivers: Array<string>;
  /** 宛先(LINE) */
  receiversByLine: Array<string>;
  /** 宛先(MAIL) */
  receiversByMail: Array<string>;
  /** 宛先グループの情報 */
  sendMessageGroupInfos: Array<SendMessageGroupInfo> | null;
  /** 利用者グループ情報 */
  residentGroupInfos: Array<ResidentGroupInfo> | null;
  /** 施設関係者グループ情報 */
  otherUserGroupInfos: Array<GroupOtherUserGroupInfo> | null;
  /** 文書単位 */
  unit: Unit | null;
  /** 文書種類カテゴリID */
  categoryId: number | null;
  /** タイトル */
  title: string | null;
  /** 本文 */
  text: string | null;
  /** 確認しましたボタンを付与するか */
  needConfirm: boolean;
  /** 同意しましたボタンを付与するか */
  needAgreement: boolean;
  /** 文書の S3URL */
  fileUrls: Array<FileUrl>;
}

export interface SendDocumentPatchModel {
  /** 公開状態 */
  isPublic: boolean;
  /** 宛先種別 */
  type: Type | null;
  /** 宛先 */
  receivers: Array<string>;
  /** 宛先(LINE) */
  receiversByLine: Array<string>;
  /** 宛先(MAIL) */
  receiversByMail: Array<string>;
  /** 宛先グループの情報 */
  sendMessageGroupInfos: Array<SendMessageGroupInfo> | null;
  /** 利用者グループ情報 */
  residentGroupInfos: Array<ResidentGroupInfo> | null;
  /** 施設関係者グループ情報 */
  otherUserGroupInfos: Array<GroupOtherUserGroupInfo> | null;
  /** 文書単位 */
  unit: Unit | null;
  /** 文書種類カテゴリID */
  categoryId: number | null;
  /** タイトル */
  title: string | null;
  /** 本文 */
  text: string | null;
  /** 確認しましたボタンを付与するか */
  needConfirm: boolean;
  /** 同意しましたボタンを付与するか */
  needAgreement: boolean;
  /** 文書の S3URL */
  fileUrls: Array<FileUrl>;
  /** 更新日時 */
  updated: number;
}

export interface SendDocumentSendPostModel {
  /** 宛先種別 */
  type: number | null;
  /** 宛先 */
  receivers: Array<string>;
  /** 宛先(LINE) */
  receiversByLine: Array<string>;
  /** 宛先(MAIL) */
  receiversByMail: Array<string>;
  /** 宛先グループの情報 */
  sendMessageGroupInfos: Array<SendMessageGroupInfo> | null;
  /** 利用者グループ情報 */
  residentGroupInfos: Array<ResidentGroupInfo> | null;
  /** 施設関係者グループ情報 */
  otherUserGroupInfos: Array<GroupOtherUserGroupInfo> | null;
  /** 文書単位 */
  unit: number | null;
  /** 文書種類カテゴリID */
  categoryId: number | null;
  /** タイトル */
  title: string | null;
  /** 本文 */
  text: string | null;
  /** 確認 */
  needConfirm: boolean;
  /** 同意 */
  needAgreement: boolean;
  /** 添付ファイル */
  fileUrls: Array<FileUrl>;
}

export interface SendDocumentRemindPostModel {
  /** 宛先 */
  receivers: Array<string>;
  /** 宛先(LINE) */
  receiversByLine: Array<string>;
  /** 宛先(MAIL) */
  receiversByMail: Array<string>;
  /** タイトル */
  title: string;
  /** 本文 */
  text: string;
}

export interface SendDocumentReplyGetModel {
  /** 施設ID */
  facilityId: number;
  /** ソートキー */
  sendDocumentsRepliesSortKey: string;
  /** sendDocumentsテーブルのID */
  parentSendDocumentsMainId: number;
  /** 利用者IDもしくはグループID */
  sortKeyPartOne: number;
  /** 家族IDもしくはメンバーID */
  sortKeyPartTwo: number;
  /** 送信者種別（0: 施設、1: 家族もしくは施設関係者） */
  senderType: number;
  /** 返信日時 */
  datetime: number;
  /** 返信内容 */
  message: string;
  /** 送信元の送信日時 */
  sourceDatetime: number;
  /** 返信者家族情報 */
  residentInfo: ResidentInfo | null;
  /** 返信者施設関係者情報 */
  otherUserGroupInfo: OtherUserGroupInfo | null;

  /** 論理削除フラグ */
  deleteFlag: boolean;
  /** 最終更新日 */
  updated: number;
  /** 作成した日付(YYYYMMDDhhmmssSSS) */
  created: number;
}

/**
 * ### 文書送付の返信者家族情報
 */
export interface ResidentInfo {
  /** 利用者ID */
  id: number;
  /** 利用者グループID */
  groupId: number;
  /** 利用者グループ名 */
  groupName: string;
  /** 利用者名 */
  name: string;
  /** 利用者の部屋ID */
  roomId: number;
  /** 利用者の部屋名 */
  room: string;
  /** 送信種別 */
  senderType: SenderType;
  /** 家族ユーザー名 */
  familyName: string;
  /** 家族ユーザーの電話番号 */
  familyTel: string;
  /** 家族ユーザーの続柄 */
  familyRelation: string;
  /** 家族ユーザーの役割 */
  familyPosition: string;
  /** 家族LINEID */
  familyLineId: string | null;
  /** 家族メールアドレス */
  familyMailaddress: string | null;
  /** 保険者番号 */
  insuredNumber: string | null;
  /** 介護保険被保険者番号 */
  careInsuranceInsuredNumber: string | null;
}

/**
 * ### 文書送付の返信者施設関係者情報
 */
export interface OtherUserGroupInfo {
  /** 施設関係者ID */
  id: number;
  /** グループ名 */
  name: string;
  /** グループ種類ID */
  typeId: number;
  /** グループ種類名 */
  typeName: string;
  /** メンバーID */
  memberId: number;
  /** メンバー名 */
  memberName: string;
  /** メンバーの電話番号 */
  memberTel: string;
  /** メンバーの所属 */
  memberOrganization: string;
  /** 送信種別 */
  senderType: SenderType;
  /** LINEID */
  memberLineId: string | null;
  /** メールアドレス */
  memberMailaddress: string | null;
}

export interface ReceiverDetail {
  /** 受信者のLINE ID */
  lineId?: string;
  /** 受信者のメールアドレス */
  mailaddress?: string;
  /** ソートキー */
  sortKey: string;
  /** 受信者名 */
  name: string;
}
